import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { WIZARD } from "@/constants/testIds";
import { TEMPLATES, VARIANTS, PALETTES, FONT_PAIRS, VOICES } from "@/lib/design";

const STEPS = ["identity", "template", "variant", "look", "voice", "review"];

export default function SiteWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    niche: "us-finance",
    template: "tribune",
    variant: "v1",
    palette: "newsprint",
    font_pair: "pair_a",
    voice: "wire-service-neutral",
    site_type: "news",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isStatic = form.site_type === "static";
  // Static lead-gen sites have no template/variant/look/voice — jump identity → review.
  const goNext = () => setStep((s) => (isStatic && s === 0 ? STEPS.length - 1 : Math.min(STEPS.length - 1, s + 1)));
  const goPrev = () => setStep((s) => (isStatic && s === STEPS.length - 1 ? 0 : Math.max(0, s - 1)));

  const variants = VARIANTS[form.template] || [];

  const canNext = useMemo(() => {
    if (step === 0) return form.name.trim().length > 0;
    return true;
  }, [step, form]);

  async function submit() {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/sites", form);
      nav(`/admin/sites/${r.data.id}`, { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create site");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black tracking-tight">Forge a New Site</h1>
        <div className="text-zinc-500 text-sm mt-1">
          Step <span className="mono text-zinc-300">{step + 1}/{STEPS.length}</span> — {STEPS[step]}
        </div>
        <div className="mt-4 grid grid-cols-6 gap-1.5">
          {STEPS.map((name, i) => (
            <div key={name} className={`h-1 rounded-sm ${i <= step ? "bg-white" : "bg-zinc-800"}`} />
          ))}
        </div>
      </div>

      <div data-testid={WIZARD.step(step)} className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-8 min-h-[420px]">
        {step === 0 && (
          <div className="space-y-6 max-w-xl">
            <div>
              <div className="label-tiny mb-2">Site type</div>
              <select
                data-testid="wizard-site-type"
                value={form.site_type}
                onChange={(e) => set("site_type", e.target.value)}
                className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2.5 rounded-sm text-sm"
              >
                <option value="news">News network (RSS feeds + AI rewrites)</option>
                <option value="static">Static lead-gen (loan request clone, no feeds)</option>
              </select>
              {isStatic && (
                <div className="text-[11px] text-zinc-500 mt-1">12 fixed pages, random light palette + fonts, no images; copy is lightly reworded once by AI. Template / look / voice steps are skipped.</div>
              )}
            </div>
            <div>
              <div className="label-tiny mb-2">Site Name</div>
              <input
                data-testid={WIZARD.nameInput}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Frontier Finance Daily"
                className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2.5 rounded-sm font-display text-xl"
              />
            </div>
            <div>
              <div className="label-tiny mb-2">Tagline</div>
              <input
                data-testid={WIZARD.taglineInput}
                value={form.tagline}
                onChange={(e) => set("tagline", e.target.value)}
                placeholder="Markets you can trade tomorrow."
                className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2.5 rounded-sm text-sm"
              />
            </div>
            <div>
              <div className="label-tiny mb-2">Niche Label</div>
              <input
                data-testid={WIZARD.nicheInput}
                value={form.niche}
                onChange={(e) => set("niche", e.target.value)}
                placeholder="us-finance"
                className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2.5 rounded-sm text-sm font-mono"
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <div className="label-tiny mb-4">Choose Template</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  data-testid={WIZARD.templateCard(t.key)}
                  onClick={() => { set("template", t.key); set("variant", VARIANTS[t.key][0].key); }}
                  className={`text-left border bg-zinc-950 rounded-sm overflow-hidden transition-all ${
                    form.template === t.key ? "border-white" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="aspect-[4/3] bg-zinc-900 overflow-hidden">
                    <img src={t.preview} alt={t.name} className="w-full h-full object-cover opacity-90" />
                  </div>
                  <div className="p-4">
                    <div className="font-display text-xl font-bold">{t.name}</div>
                    <div className="text-xs text-zinc-400 mt-1">{t.tagline}</div>
                    <div className="label-tiny mt-3">Best for</div>
                    <div className="text-xs text-zinc-500">{t.bestFor}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="label-tiny mb-4">Choose Variant for {form.template}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {variants.map((v) => (
                <button
                  key={v.key}
                  data-testid={WIZARD.variantCard(v.key)}
                  onClick={() => set("variant", v.key)}
                  className={`text-left border bg-zinc-950 p-5 rounded-sm transition-all ${
                    form.variant === v.key ? "border-white" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="mono text-[11px] text-zinc-500 uppercase">{v.key}</div>
                  <div className="font-display text-xl font-bold mt-1">{v.name}</div>
                  <div className="text-xs text-zinc-400 mt-2">{v.note}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8">
            <div>
              <div className="label-tiny mb-4">Palette</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(PALETTES).map(([key, p]) => (
                  <button
                    key={key}
                    data-testid={WIZARD.paletteCard(key)}
                    onClick={() => set("palette", key)}
                    className={`border rounded-sm overflow-hidden transition-all ${
                      form.palette === key ? "border-white" : "border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <div className="h-20 flex" style={{ background: p.bg }}>
                      <div className="flex-1" style={{ background: p.bg }} />
                      <div className="flex-1" style={{ background: p.surface }} />
                      <div className="flex-1" style={{ background: p.accent }} />
                    </div>
                    <div className="p-2 text-left bg-zinc-950">
                      <div className="text-xs text-zinc-200">{p.label}</div>
                      <div className="mono text-[10px] text-zinc-500">{key}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="label-tiny mb-4">Font Pair</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(FONT_PAIRS).map(([key, fp]) => (
                  <button
                    key={key}
                    data-testid={WIZARD.fontCard(key)}
                    onClick={() => set("font_pair", key)}
                    className={`border bg-zinc-950 p-4 rounded-sm transition-all text-left ${
                      form.font_pair === key ? "border-white" : "border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <div className="mono text-[10px] text-zinc-500 uppercase mb-2">{key}</div>
                    <div className="text-xl truncate" style={{ fontFamily: fp.display }}>The Wire</div>
                    <div className="text-xs text-zinc-400 mt-1" style={{ fontFamily: fp.body }}>
                      {fp.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="label-tiny mb-4">Editorial Voice</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {VOICES.map((v) => (
                <button
                  key={v.key}
                  data-testid={WIZARD.voiceCard(v.key)}
                  onClick={() => set("voice", v.key)}
                  className={`border bg-zinc-950 p-5 rounded-sm transition-all text-left ${
                    form.voice === v.key ? "border-white" : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <div className="font-display text-lg font-bold">{v.label}</div>
                  <div className="text-xs text-zinc-400 mt-1">{v.note}</div>
                  <div className="mono text-[10px] text-zinc-500 mt-2">{v.key}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4">
            <div className="label-tiny mb-2">Review</div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["Type", isStatic ? "Static lead-gen (loan clone)" : "News network"],
                ["Name", form.name],
                ["Tagline", form.tagline || "—"],
                ["Niche", form.niche],
                ...(isStatic ? [["Design", "random light palette + font pair"], ["Copy", "AI reword once at creation"]] : [
                  ["Template", `${form.template} / ${form.variant}`],
                  ["Palette", PALETTES[form.palette]?.label],
                  ["Font pair", FONT_PAIRS[form.font_pair]?.label],
                  ["Voice", VOICES.find(v => v.key === form.voice)?.label],
                ]),
              ].map(([k, v]) => (
                <div key={k} className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/40">
                  <div className="label-tiny">{k}</div>
                  <div className="mono text-sm text-zinc-100 mt-1">{v}</div>
                </div>
              ))}
            </dl>
            {err && <div className="text-xs text-red-400 mono">{err}</div>}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          data-testid={WIZARD.prev}
          disabled={step === 0 || busy}
          onClick={goPrev}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm text-zinc-300 hover:text-white disabled:opacity-30"
        >
          <ChevronLeft size={16} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            data-testid={WIZARD.next}
            disabled={!canNext}
            onClick={goNext}
            className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 px-5 py-2 rounded-sm text-sm font-semibold"
          >
            Next <ChevronRight size={16} />
          </button>
        ) : (
          <button
            data-testid={WIZARD.create}
            disabled={busy}
            onClick={submit}
            className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 px-5 py-2 rounded-sm text-sm font-semibold"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={16} />}
            Create Site
          </button>
        )}
      </div>
    </div>
  );
}
